import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { LesenStatementCompareContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

const VERDICTS = ["discussed", "contradicts", "absent"] as const;

/** Read a text, then judge each statement: discussed / contradicted / absent
 *  (mirrors iOS LesenStatementCompareView). Answer is a per-statement verdict
 *  map. */
export function LesenStatementCompareRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  sharedPassageText,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as LesenStatementCompareContent;
  const map = (answer as Record<string, string>) ?? {};
  const passage = sharedPassageText && sharedPassageText.trim() ? sharedPassageText : c.passage?.text;
  const verdictLabel: Record<string, string> = {
    discussed: t("Discussed"),
    contradicts: t("Contradicts"),
    absent: t("Not mentioned"),
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      {c.passage?.title && <div className="mt-3 text-sm font-semibold text-ink">{c.passage.title}</div>}
      {passage && (
        <div className="mt-1 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-ink">{passage}</div>
      )}
      <div className="mt-4 space-y-3">
        {(c.statements ?? []).map((s) => {
          const chosen = map[s.id];
          return (
            <div key={s.id} className="rounded-xl border border-black/10 p-3">
              <div className="mb-2 text-sm text-ink">{s.text}</div>
              <div className="flex flex-wrap gap-2">
                {VERDICTS.map((v) => {
                  let tone = "border-black/10 bg-white hover:border-brand-300";
                  if (revealed) {
                    if (v === s.verdict) tone = "border-emerald-400 bg-emerald-50 text-emerald-800";
                    else if (v === chosen) tone = "border-red-400 bg-red-50 text-red-700";
                    else tone = "border-black/10 bg-white opacity-60";
                  } else if (v === chosen) tone = "border-brand-500 bg-brand-50 ring-1 ring-brand-300";
                  return (
                    <button
                      key={v}
                      type="button"
                      disabled={revealed}
                      onClick={() => setAnswer({ ...map, [s.id]: v })}
                      className={cx("rounded-lg border px-3 py-1.5 text-sm font-medium transition disabled:cursor-default", tone)}
                    >
                      {verdictLabel[v]}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
